import React from 'react'
import { cn } from '../../utils/cn'

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Visual variant
   */
  variant?: 'default' | 'elevated' | 'interactive' | 'gradient' | 'selection'

  /**
   * Gradient color (only for variant="gradient")
   */
  gradientColor?: 'gold' | 'green' | 'coral' | 'brown'

  /**
   * Selected state (only for variant="selection")
   */
  selected?: boolean

  /**
   * Padding size
   */
  padding?: 'none' | 'sm' | 'md' | 'lg'

  /**
   * Make card clickable (adds hover effects)
   */
  clickable?: boolean
}

const cardVariants = {
  variant: {
    default: 'bg-butter-cream border border-soft-tan',
    elevated: 'bg-white border border-soft-tan shadow-md',
    interactive: 'bg-white border border-soft-tan hover:shadow-md transition-shadow cursor-pointer',
    gradient: '', // Handled separately by gradientColor
    selection: 'border-2 transition-colors cursor-pointer',
  },
  gradientColor: {
    gold: 'bg-gradient-to-br from-toast-gold to-toast-gold/90 hover:from-toast-gold/90 hover:to-toast-gold/80 text-white border-0 shadow-lg',
    green: 'bg-gradient-to-br from-avocado-green to-avocado-green/90 hover:from-avocado-green/90 hover:to-avocado-green/80 text-white border-0 shadow-lg',
    coral: 'bg-gradient-to-br from-coral-pop to-coral-pop/90 hover:from-coral-pop/90 hover:to-coral-pop/80 text-white border-0 shadow-lg',
    brown: 'bg-gradient-to-br from-deep-charcoal to-deep-charcoal/90 hover:from-deep-charcoal/90 hover:to-deep-charcoal/80 text-white border-0 shadow-lg',
  },
  padding: {
    none: 'p-0',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  },
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      variant = 'default',
      gradientColor,
      selected = false,
      padding = 'md',
      clickable = false,
      children,
      ...props
    },
    ref
  ) => {
    // Selection variant styles
    const selectionStyles =
      variant === 'selection'
        ? selected
          ? 'border-avocado-green bg-avocado-green/10 text-deep-charcoal'
          : 'border-soft-tan hover:border-soft-tan/70'
        : ''

    // Gradient variant styles
    const gradientStyles =
      variant === 'gradient' && gradientColor
        ? cardVariants.gradientColor[gradientColor]
        : ''

    // Interactive/clickable enhancements
    const interactiveStyles = (clickable || variant === 'interactive' || variant === 'gradient')
      ? 'transform hover:scale-105 transition-transform cursor-pointer'
      : ''

    return (
      <div
        ref={ref}
        className={cn(
          // Base styles
          'rounded-lg',

          // Variant styles
          variant === 'gradient' ? gradientStyles : cardVariants.variant[variant],

          // Selection styles
          selectionStyles,

          // Padding
          cardVariants.padding[padding],

          // Interactive
          interactiveStyles,

          // Custom className
          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

export default Card
